import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Store, Clock, MapPin, Sparkles } from 'lucide-react';
import { navigate } from '../App';

/**
 * WhatsHappeningTodayPopup - Shows businesses open today and events happening today
 * Displays when user enters app or selects a zone
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether popup is visible
 * @param {Function} props.onClose - Close handler
 * @param {Array} props.openBusinesses - List of businesses open today
 * @param {Array} props.todayEvents - List of events happening today
 * @param {string} props.zoneName - Optional zone name
 */
export const WhatsHappeningTodayPopup = ({
  isOpen,
  onClose,
  openBusinesses = [],
  todayEvents = [],
  zoneName = null
}) => {
  const [dontShowToday, setDontShowToday] = React.useState(false);
  const hasContent = openBusinesses.length > 0 || todayEvents.length > 0;

  // Don't render if no content
  if (!hasContent) {
    return null;
  }

  const handleClose = () => {
    if (dontShowToday) {
      // Store current date in localStorage
      const today = new Date().toDateString();
      localStorage.setItem('whatsHappeningPopup_hideUntil', today);
      console.log('✅ User chose to hide popup until:', today);
    }
    onClose();
  };

  const handleBusinessClick = (businessId) => {
    handleClose();
    navigate(`?entity=${businessId}`);
  };

  const formatEventTime = (eventTime) => {
    try {
      const date = new Date(eventTime);
      return date.toLocaleTimeString('he-IL', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return '';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000] flex items-start md:items-center justify-center p-4 md:py-8 animate-fade-in overflow-y-auto"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl my-4 md:my-8 relative max-h-[92vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            {/* Header */}
            <div className="relative bg-gradient-to-r from-mekomit-500 via-mekomit-400 to-warning-400 p-6 md:p-8 rounded-t-3xl">
              <button
                onClick={handleClose}
                className="absolute top-4 left-4 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors backdrop-blur-sm"
                aria-label="סגור"
              >
                <X className="w-5 h-5 text-white" />
              </button>

              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Sparkles className="w-8 h-8 text-white" />
                  <h2 className="text-2xl md:text-3xl font-bold text-white">
                    היום בקהילה
                  </h2>
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                {zoneName && (
                  <p className="text-white/95 font-medium flex items-center justify-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {zoneName}
                  </p>
                )}
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 md:p-8">
              {/* Events Section */}
              {todayEvents.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary-500 to-secondary-400 flex items-center justify-center shadow-md">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-ink-strong">
                      מפגשים היום ({todayEvents.length})
                    </h3>
                  </div>

                  <div className="space-y-3">
                    {todayEvents.map((event, index) => (
                      <motion.button
                        key={event.business_id || index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => handleBusinessClick(event.business_id)}
                        className="w-full bg-gradient-to-r from-secondary-50 to-brand-50 rounded-2xl p-4 border border-secondary-200 hover:shadow-lg transition-all duration-300 hover:scale-[1.02] text-right"
                      >
                        <div className="flex items-start gap-3">
                          {event.business_images && event.business_images[0] && (
                            <img
                              src={event.business_images[0]}
                              alt={event.business_name}
                              className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-lg text-secondary-700 mb-1 truncate">
                              {event.business_name}
                            </h4>
                            {event.event_time && (
                              <div className="flex items-center gap-2 text-sm text-ink-muted">
                                <Clock className="w-4 h-4" />
                                <span>{formatEventTime(event.event_time)}</span>
                              </div>
                            )}
                            {event.business_description && (
                              <p className="text-sm text-ink-muted mt-2 line-clamp-2">
                                {event.business_description}
                              </p>
                            )}
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {/* Businesses Section */}
              {openBusinesses.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-mekomit-500 to-mekomit-400 flex items-center justify-center shadow-md">
                      <Store className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-ink-strong">
                      עסקים פתוחים היום ({openBusinesses.length})
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {openBusinesses.slice(0, 8).map((business, index) => (
                      <motion.button
                        key={business.business_id || index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => handleBusinessClick(business.business_id)}
                        className="bg-gradient-to-r from-mekomit-50 to-brand-50 rounded-2xl p-4 border border-mekomit-200 hover:shadow-lg transition-all duration-300 hover:scale-[1.02] text-right"
                      >
                        <div className="flex items-center gap-3">
                          {business.business_images && business.business_images[0] && (
                            <img
                              src={business.business_images[0]}
                              alt={business.business_name}
                              className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-mekomit-700 truncate">
                              {business.business_name}
                            </h4>
                            {business.business_category && business.business_category[0] && (
                              <p className="text-xs text-ink-muted truncate">
                                {business.business_category[0]}
                              </p>
                            )}
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </div>

                  {openBusinesses.length > 8 && (
                    <p className="text-center text-sm text-ink-subtle mt-4">
                      ועוד {openBusinesses.length - 8} עסקים נוספים פתוחים היום
                    </p>
                  )}
                </div>
              )}

              {/* Summary Message */}
              <div className="mt-6 bg-gradient-to-r from-mekomit-50 to-warning-50 rounded-2xl p-4 border border-mekomit-200 text-center">
                <p className="text-ink font-medium">
                  {todayEvents.length + openBusinesses.length} אפשרויות מחכות לך היום!
                </p>
                <p className="text-sm text-ink-muted mt-1">
                  לחץ על כל אחד מהם לפרטים נוספים
                </p>
              </div>
            </div>

            {/* Fixed Bottom Action */}
            <div className="p-6 pt-4 border-t border-line rounded-b-3xl bg-white space-y-4">
              {/* Don't Show Today Option - Prominent */}
              <div className="bg-gradient-to-r from-lime-50 to-success-50 border-2 border-lime-300 rounded-xl p-4 shadow-sm">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={dontShowToday}
                    onChange={(e) => setDontShowToday(e.target.checked)}
                    className="w-6 h-6 rounded-lg border-2 border-lime-400 text-lime-600 focus:ring-2 focus:ring-lime-400 focus:ring-offset-2 cursor-pointer transition-all"
                  />
                  <div className="flex-1">
                    <span className="text-base font-bold text-success-700 group-hover:text-emerald-950 transition-colors block">
                      אל תציג שוב היום
                    </span>
                    <span className="text-xs text-success-700 mt-0.5 block">
                      החלון יופיע שוב מחר
                    </span>
                  </div>
                </label>
              </div>

              <button
                onClick={handleClose}
                className="w-full px-6 py-3 bg-gradient-to-r from-mekomit-500 to-mekomit-400 text-white rounded-xl hover:from-mekomit-600 hover:to-mekomit-500 transition-all shadow-lg hover:shadow-xl font-bold text-lg"
              >
                סגור
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default WhatsHappeningTodayPopup;
